import { useEffect, useState } from "react";
import {
  X, Save, Loader2, Globe2, ScrollText, ShieldCheck, AlertTriangle, CheckCircle2,
  XCircle, Users, Bell,
} from "lucide-react";
import api from "@/lib/api";

/**
 * Phase 8.2 — Security Settings Modal (Geo-Shield + Terms Version).
 *
 * Two stacked sections (no separate tab strip — keeps the form in one form
 * submission per concern):
 *   1) Geo-Shield        → /saas/admin/geo-config
 *   2) Terms Version     → /saas/admin/terms-config
 *
 * Style mirrors StripeSettingsModal so operators have a consistent admin UX.
 */
export default function SecuritySettingsModal({ open, onClose, onSaved }) {
  const [section, setSection] = useState("geo"); // 'geo' | 'terms'

  // ---- Geo state ----
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoSaving,  setGeoSaving]  = useState(false);
  const [geoErr, setGeoErr] = useState("");
  const [geoOk,  setGeoOk]  = useState("");
  const [geoEnabled, setGeoEnabled] = useState(false);
  const [geoCountries, setGeoCountries] = useState([]); // string[] uppercase ISO codes
  const [geoDraft, setGeoDraft] = useState("");          // currently typed code (1–2 chars)
  const [geoMeta, setGeoMeta] = useState({ updated_at: null, updated_by: null, detected_country: "XX" });

  // ---- Terms state ----
  const [termsLoading, setTermsLoading] = useState(false);
  const [termsSaving, setTermsSaving] = useState(false);
  const [termsErr, setTermsErr] = useState("");
  const [termsOk, setTermsOk] = useState("");
  const [termsVersion, setTermsVersion] = useState("");
  const [termsConfig, setTermsConfig] = useState(null); // {default_version, is_overridden, updated_at}
  const [termsStats, setTermsStats] = useState(null);   // {total, pending, accepted}

  useEffect(() => {
    if (!open) return;
    fetchGeo();
    fetchTerms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchGeo = async () => {
    setGeoLoading(true); setGeoErr(""); setGeoOk("");
    try {
      const res = await api.get("/saas/admin/geo-config");
      const c = res.data?.config || {};
      setGeoEnabled(!!c.enabled);
      setGeoCountries((c.allowed_countries || []).map(x => String(x).toUpperCase()));
      setGeoDraft("");
      setGeoMeta({
        updated_at:       c.updated_at,
        updated_by:       c.updated_by,
        detected_country: res.data?.detected_country || "XX",
      });
    } catch (e) {
      setGeoErr(e?.response?.data?.detail || "Geo-config yüklenemedi.");
    } finally { setGeoLoading(false); }
  };

  const fetchTerms = async () => {
    setTermsLoading(true); setTermsErr(""); setTermsOk("");
    try {
      const res = await api.get("/saas/admin/terms-config");
      setTermsConfig(res.data?.config || null);
      setTermsStats(res.data?.stats || null);
      setTermsVersion(res.data?.config?.is_overridden ? (res.data.config.current_version || "") : "");
    } catch (e) {
      setTermsErr(e?.response?.data?.detail || "Terms-config yüklenemedi.");
    } finally { setTermsLoading(false); }
  };

  const submitGeo = async (e) => {
    e.preventDefault();
    setGeoSaving(true); setGeoErr(""); setGeoOk("");
    // Eğer kullanıcı taslakta yazılı bir kod bıraktıysa save sırasında otomatik commit et
    const draft = geoDraft.trim().toUpperCase();
    let list = [...geoCountries];
    if (/^[A-Z]{2}$/.test(draft) && !list.includes(draft)) list.push(draft);
    list = Array.from(new Set(list.map(s => s.toUpperCase()).filter(s => /^[A-Z]{2}$/.test(s))));
    try {
      const res = await api.put("/saas/admin/geo-config", {
        enabled: geoEnabled,
        allowed_countries: list,
      });
      const saved = (res.data?.config?.allowed_countries || []).map(x => String(x).toUpperCase());
      setGeoCountries(saved);
      setGeoDraft("");
      setGeoOk(`Kaydedildi. Aktif ülke sayısı: ${saved.length}.`);
      onSaved && onSaved();
      // Re-fetch to refresh updated_at/by metadata only
      fetchGeo();
    } catch (e2) {
      setGeoErr(e2?.response?.data?.detail || "Kaydedilemedi.");
    } finally { setGeoSaving(false); }
  };

  // ---- Geo chip helpers ----
  const addGeoCountry = (raw) => {
    const code = String(raw || "").trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(code)) return false;
    setGeoCountries(prev => prev.includes(code) ? prev : [...prev, code]);
    return true;
  };
  const removeGeoCountry = (code) => {
    setGeoCountries(prev => prev.filter(c => c !== code));
  };
  const onGeoDraftKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === " ") {
      e.preventDefault();
      if (addGeoCountry(geoDraft)) setGeoDraft("");
    } else if (e.key === "Backspace" && geoDraft === "" && geoCountries.length > 0) {
      // Backspace on empty input removes last chip
      setGeoCountries(prev => prev.slice(0, -1));
    }
  };
  const onGeoDraftChange = (e) => {
    const v = e.target.value;
    // Auto-commit when user types/pastes "TR," or "TR " (separator at the end)
    if (/[, \t]$/.test(v)) {
      if (addGeoCountry(v.replace(/[, \t]+$/, ""))) {
        setGeoDraft("");
        return;
      }
    }
    // Comma-separated paste: split & add multiple at once
    if (v.includes(",")) {
      const parts = v.split(",");
      parts.slice(0, -1).forEach(p => addGeoCountry(p));
      setGeoDraft(parts[parts.length - 1].toUpperCase().slice(0, 2));
      return;
    }
    setGeoDraft(v.toUpperCase().slice(0, 2));
  };

  const submitTerms = async (e) => {
    e.preventDefault();
    setTermsSaving(true); setTermsErr(""); setTermsOk("");
    try {
      const res = await api.put("/saas/admin/terms-config", {
        current_version: termsVersion.trim(),
      });
      setTermsOk(`Kaydedildi. Yeni sürüm: ${res.data?.current_version}. Tüm müşterilerden yeniden onay istenecek.`);
      onSaved && onSaved();
      fetchTerms();
    } catch (e2) {
      setTermsErr(e2?.response?.data?.detail || "Kaydedilemedi.");
    } finally { setTermsSaving(false); }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/75 backdrop-blur-sm grid place-items-center p-6"
      onClick={onClose}
      data-testid="security-settings-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-slate-900 border border-amber-500/30 rounded-2xl shadow-2xl shadow-amber-500/10 max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="security-settings-modal"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-amber-400" /> Güvenlik Yapılandırması
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">Geo-Shield (ülke koruması) + Sözleşme sürümü dayatması.</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white" data-testid="security-settings-close">
            <X size={18} />
          </button>
        </div>

        {/* Section tabs */}
        <div className="flex border-b border-slate-800 bg-slate-950/60">
          <SectionTab id="geo" current={section} setSection={setSection} icon={Globe2} label="Geo-Shield" />
          <SectionTab id="terms" current={section} setSection={setSection} icon={ScrollText} label="Sözleşme Sürümü" />
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto">
          {section === "geo" && (
            <form onSubmit={submitGeo} className="space-y-5" data-testid="security-geo-form">
              {/* Detected country */}
              <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-700 bg-slate-950/40">
                <Globe2 className="w-4 h-4 text-amber-400" />
                <div className="text-xs">
                  <div className="font-mono font-bold uppercase tracking-wider text-white">
                    Şu an algılanan ülke: <span className="text-amber-400">{geoMeta.detected_country}</span>
                  </div>
                  <div className="text-gray-500">CF-IPCountry header'ından okundu (Cloudflare standardı).</div>
                </div>
              </div>

              {/* Enable toggle */}
              <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-800 bg-slate-950/40 cursor-pointer hover:border-amber-500/40 transition" data-testid="security-geo-enabled-row">
                <input
                  type="checkbox"
                  checked={geoEnabled}
                  onChange={(e) => setGeoEnabled(e.target.checked)}
                  className="mt-1 w-5 h-5 accent-amber-500"
                  data-testid="security-geo-enabled"
                />
                <div className="flex-1">
                  <div className="text-sm font-bold text-white">Geo-Shield Aktif</div>
                  <div className="text-[11px] text-gray-500">
                    Açıkken aşağıdaki listede olmayan ülkelerden gelen müşteri istekleri 403 ile engellenir.
                    Admin uç noktaları etkilenmez.
                  </div>
                </div>
              </label>

              {/* Allowed countries — chip-based creatable input */}
              <div>
                <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                  İzin Verilen Ülke Kodları
                </label>
                <div
                  className="mt-1.5 w-full bg-slate-950 border border-slate-800 rounded-lg p-2.5 text-white text-sm focus-within:border-amber-500 transition flex flex-wrap items-center gap-1.5 cursor-text min-h-[48px]"
                  onClick={() => document.getElementById("geo-country-input")?.focus()}
                  data-testid="security-geo-chips"
                >
                  {geoCountries.map((code) => (
                    <span
                      key={code}
                      className="inline-flex items-center gap-1 px-2 h-7 rounded-md bg-amber-500/15 border border-amber-500/40 text-amber-300 font-mono text-xs uppercase tracking-wider"
                      data-testid={`security-geo-chip-${code}`}
                    >
                      {code}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removeGeoCountry(code); }}
                        className="text-amber-400/70 hover:text-red-400 transition"
                        title={`${code} kaldır`}
                        data-testid={`security-geo-chip-remove-${code}`}
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                  <input
                    id="geo-country-input"
                    type="text"
                    autoComplete="off"
                    autoCapitalize="characters"
                    spellCheck={false}
                    value={geoDraft}
                    onChange={onGeoDraftChange}
                    onKeyDown={onGeoDraftKeyDown}
                    placeholder={geoCountries.length === 0 ? "TR yazıp Enter'a basın" : "+ Ülke ekle"}
                    className="flex-1 min-w-[120px] bg-transparent outline-none border-0 px-1 h-7 font-mono uppercase placeholder:text-gray-600 placeholder:normal-case"
                    data-testid="security-geo-input"
                  />
                </div>
                <p className="text-[10px] text-gray-500 mt-1">
                  2 harfli ISO 3166-1 alpha-2 kodu yazıp <span className="font-mono text-amber-400">Enter</span>,{" "}
                  <span className="font-mono text-amber-400">virgül</span> veya <span className="font-mono text-amber-400">boşluk</span>'a basarak ekleyin. Chip'in yanındaki <span className="text-amber-400">×</span> ile kaldırın.
                </p>
                {geoEnabled && geoCountries.length === 0 && (
                  <div className="mt-2 flex items-start gap-2 text-[11px] text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-2.5">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>Boş liste + aktif Geo-Shield = <strong>tüm müşteriler engellenir</strong> (deny-all). En az bir ülke ekleyin.</span>
                  </div>
                )}
              </div>

              {/* Meta */}
              {geoMeta.updated_at && (
                <div className="text-[10px] text-gray-500 font-mono">
                  Son güncelleme: {new Date(geoMeta.updated_at).toLocaleString()} · {geoMeta.updated_by || "-"}
                </div>
              )}

              {geoErr && <Banner kind="error">{geoErr}</Banner>}
              {geoOk  && <Banner kind="ok">{geoOk}</Banner>}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="submit"
                  disabled={geoSaving || geoLoading}
                  className="px-5 h-10 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-bold flex items-center gap-2 disabled:opacity-50 transition"
                  data-testid="security-geo-save"
                >
                  {geoSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save size={14} />}
                  {geoSaving ? "Kaydediliyor…" : "Geo-Shield Ayarlarını Kaydet"}
                </button>
              </div>
            </form>
          )}

          {section === "terms" && (
            <form onSubmit={submitTerms} className="space-y-5" data-testid="security-terms-form">
              {/* Current effective version */}
              <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-700 bg-slate-950/40">
                <ScrollText className="w-4 h-4 text-amber-400" />
                <div className="flex-1 text-xs">
                  <div className="font-mono font-bold uppercase tracking-wider text-white">
                    Geçerli sürüm: <span className="text-amber-400">{termsConfig?.current_version || "—"}</span>
                  </div>
                  <div className="text-gray-500">
                    {termsConfig?.is_overridden
                      ? "DB override aktif (admin tarafından bump'lanmış)."
                      : <>Default kod sabitinden geliyor: <span className="font-mono">{termsConfig?.default_version}</span>.</>}
                  </div>
                </div>
              </div>

              {/* Stats — kaç müşteri yeni sürümü onaylamamış */}
              {termsStats && (
                <div className="grid grid-cols-3 gap-3" data-testid="security-terms-stats">
                  <Stat label="Toplam müşteri" value={termsStats.total_customers} icon={Users} />
                  <Stat label="Onayı bekleyen" value={termsStats.pending_acceptance} icon={Bell} accent="amber" />
                  <Stat label="Güncel onay" value={termsStats.accepted_current_version} icon={CheckCircle2} accent="emerald" />
                </div>
              )}

              {/* Override input */}
              <div>
                <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                  Yeni Sürüm Etiketi (opsiyonel — boş bırakılırsa default'a döner)
                </label>
                <input
                  type="text"
                  value={termsVersion}
                  onChange={(e) => setTermsVersion(e.target.value)}
                  placeholder="v1.1-2026-04"
                  className="mt-1.5 w-full bg-slate-950 border border-slate-800 rounded-lg p-3 text-white text-sm focus:border-amber-500 outline-none font-mono"
                  data-testid="security-terms-version"
                />
                <p className="text-[10px] text-gray-500 mt-1">
                  Sürümü değiştirdiğinizde, mevcut sürümü onaylamış{" "}
                  <span className="text-amber-400">{termsStats?.accepted_current_version ?? 0}</span>{" "}
                  müşteri yeni sözleşmeyi onaylamadan <span className="font-mono">/dashboard</span>'a giremez.
                </p>
              </div>

              {termsErr && <Banner kind="error">{termsErr}</Banner>}
              {termsOk  && <Banner kind="ok">{termsOk}</Banner>}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="submit"
                  disabled={termsSaving || termsLoading}
                  className="px-5 h-10 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-bold flex items-center gap-2 disabled:opacity-50 transition"
                  data-testid="security-terms-save"
                >
                  {termsSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save size={14} />}
                  {termsSaving ? "Kaydediliyor…" : "Sözleşme Sürümünü Kaydet"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function SectionTab({ id, current, setSection, icon: Icon, label }) {
  const isActive = current === id;
  return (
    <button
      type="button"
      onClick={() => setSection(id)}
      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold transition border-b-2 ${
        isActive
          ? "text-amber-400 border-amber-500 bg-slate-900/60"
          : "text-gray-500 border-transparent hover:text-white hover:bg-slate-800/30"
      }`}
      data-testid={`security-section-${id}`}
    >
      <Icon size={14} />
      {label}
    </button>
  );
}

function Stat({ label, value, icon: Icon, accent }) {
  const colorMap = {
    amber:    "text-amber-400",
    emerald:  "text-emerald-400",
  };
  return (
    <div className="p-3 rounded-lg border border-slate-800 bg-slate-950/40">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-500 font-bold">
        <Icon size={11} /> {label}
      </div>
      <div className={`text-2xl font-bold mt-1 ${colorMap[accent] || "text-white"}`}>{value}</div>
    </div>
  );
}

function Banner({ kind, children }) {
  const cls = kind === "error"
    ? "bg-red-500/10 border-red-500/30 text-red-300"
    : "bg-emerald-500/10 border-emerald-500/30 text-emerald-300";
  const Icon = kind === "error" ? XCircle : CheckCircle2;
  return (
    <div className={`flex items-start gap-2 p-3 rounded-lg border text-xs ${cls}`}>
      <Icon className="w-4 h-4 shrink-0 mt-0.5" />
      <span>{children}</span>
    </div>
  );
}
